import { css } from "@emotion/css";
import { Button, Drawer } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { GenerateArgoCDAnnotationsDrawer } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/GenerateArgoCDAnnotationsDrawer";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import type { DeploymentConfiguration } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { isScopedToDeploymentConfiguration } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { ArgoCDDeploymentPreview } from "~/components/Actions/argocd/ArgoCDDeploymentPreview";
type ArgoCDDeploymentPreviewDrawerInnerProps = {
    analyticsCorrelationId?: string;
    project: ProjectResource;
    deploymentConfiguration?: DeploymentConfiguration;
    onShowGenerateAnnotationsDrawer: (applicationId: string, applicationSourceIndex: number | undefined) => void;
};
type InnerActions = {
    refresh: () => void;
};
const ArgoCDDeploymentPreviewDrawerInner = forwardRef<InnerActions, ArgoCDDeploymentPreviewDrawerInnerProps>((props: ArgoCDDeploymentPreviewDrawerInnerProps, ref) => {
    const { project, deploymentConfiguration, onShowGenerateAnnotationsDrawer } = props;
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const query = useInlineStatusQuery(async (repository) => {
        const environmentsPromise = getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsEnabled, repository);
        const tenantsPromise = repository.Tenants.all();
        const projectsPromise = repository.Projects.all();
        const instances = await repository.ArgoCDInstances.list();
        const queryFilter = { projectIds: [project.Id] };
        const applicationsResponsePromises = instances.Resources.map((instance) => repository.ArgoCDGateways.listApps(instance.GatewayId, queryFilter));
        const environments = await environmentsPromise;
        const tenants = await tenantsPromise;
        const projects = await projectsPromise;
        const applicationResponses = await Promise.all(applicationsResponsePromises);
        const filteredApps = applicationResponses
            .map((ar) => ar.Resources)
            .reduce((accumulator, value) => accumulator.concat(value), [])
            .filter((app) => isScopedToDeploymentConfiguration(app, project.Id, deploymentConfiguration));
        return {
            argoCDInstances: instances.Resources,
            applications: filteredApps,
            context: {
                allEnvironments: environments,
                allTenants: tenants,
                allProjects: projects,
            },
        };
    }, [ephemeralEnvironmentsEnabled, deploymentConfiguration, project.Id], "Fetch ArgoCD Instances and Applications");
    useImperativeHandle(ref, () => ({
        refresh: () => query.refetch(),
    }));
    return (<>
            <div className={styles.header}>
                The <b>Update Argo CD App Images</b> step in the <b>{project.Name}</b> project will update these applications:
            </div>
            <ArgoCDDeploymentPreview spaceId={project.SpaceId} query={query} onShowGenerateAnnotationsDrawer={onShowGenerateAnnotationsDrawer} showScopingHelpText={deploymentConfiguration === undefined}/>
        </>);
});
type ArgoCDDeploymentPreviewDrawerProps = Omit<ArgoCDDeploymentPreviewDrawerInnerProps, "onShowGenerateAnnotationsDrawer"> & {
    isOpen: boolean;
    onClose: () => void;
};
export const ArgoCDDeploymentPreviewDrawer = ({ isOpen, onClose, project, deploymentConfiguration, ...rest }: ArgoCDDeploymentPreviewDrawerProps) => {
    const [generateDrawerOpen, setGenerateDrawerOpen] = useState(false);
    const [initiallySelectedAnnotationsApplicationId, setInitiallySelectedAnnotationsApplicationId] = useState<string | undefined>();
    const [initiallySelectedApplicationSourceIndex, setInitiallySelectedApplicationSourceIndex] = useState<number | undefined>();
    const ref = useRef<InnerActions>(null);
    return (<Drawer isOpen={isOpen} onClose={onClose} title={"Deployment Preview"} variant="customActions" customActions={[
            <Button importance={"quiet"} label={"Close"} onClick={() => onClose()}/>,
            <Button importance={"tertiary"} label={"Refresh"} onClick={() => ref.current?.refresh()}/>,
            <Button importance={"secondary"} label={"Generate Annotations"} onClick={() => setGenerateDrawerOpen(true)}/>,
        ]} width={"wide"} layout={"none"}>
            <>
                <ArgoCDDeploymentPreviewDrawerInner {...rest} ref={ref} project={project} onShowGenerateAnnotationsDrawer={(id, applicationSourceIndex) => {
            setInitiallySelectedAnnotationsApplicationId(id);
            setInitiallySelectedApplicationSourceIndex(applicationSourceIndex);
            setGenerateDrawerOpen(true);
        }} deploymentConfiguration={deploymentConfiguration}/>
                <GenerateArgoCDAnnotationsDrawer open={generateDrawerOpen} onClose={() => {
            setGenerateDrawerOpen(false);
            setInitiallySelectedAnnotationsApplicationId(undefined);
            setInitiallySelectedApplicationSourceIndex(undefined);
        }} initiallySelectedApplicationId={initiallySelectedAnnotationsApplicationId} initiallySelectedApplicationSourceIndex={initiallySelectedApplicationSourceIndex} initiallySelectedProjectSlug={project.Slug}/>
            </>
        </Drawer>);
};
const styles = {
    header: css({
        paddingLeft: space[16],
        paddingBottom: space[16],
        paddingTop: space[16],
    }),
};
